// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetLocationFilterMenu } from '@/asset/components/asset-location-filter-menu';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  AddRounded,
  ClearRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { LocationCompanyType } from '@/constants/location-company-type';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { PurchaseOrderDialog } from '@/purchase-order/components/purchase-order-dialog';
import { PurchaseOrderModifierFilter } from '@/purchase-order/components/purchase-order-modifier-filter';
import { PurchaseOrderTable } from '@/purchase-order/components/purchase-order-table';
import { PurchaseOrderTableSkeleton } from '@/purchase-order/components/purchase-order-table-skeleton';
import { PO_STATUS_FILTER_OPTIONS } from '@/purchase-order/constants/filter-config';
import {
  PHASES_WITH_MODIFIERS,
  POPhase,
  POSubstatus,
} from '@/purchase-order/constants/status';
import {
  PurchaseOrderSearchListing,
  PurchaseOrderSortProperty,
} from '@/purchase-order/hooks/use-purchase-orders';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { debounce, omit } from 'lodash-es';
import {
  ChangeEventHandler,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/**
 * Navigation options for the purchase order listing.
 */
interface NavigateOptions {
  to?: string;
  search?: Partial<PurchaseOrderSearchListing> & Record<string, unknown>;
}

interface PurchaseOrderListingProps {
  currentStatus: POPhase | 'all';
  searchParameters: PurchaseOrderSearchListing;
  sort: Sort<PurchaseOrderSortProperty>;
  onSort: (property: PurchaseOrderSortProperty) => void;
  navigateFn: (options: NavigateOptions) => void;
}

export function PurchaseOrderListing(props: PurchaseOrderListingProps) {
  const { navigateFn, searchParameters, currentStatus, sort, onSort } = props;
  const searchContainer = useRef<HTMLInputElement>(null);
  const createDialog = useDisclosure();
  const infoTooltip = useDisclosure();
  const infoButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Debounced search query handler.
   */
  const updateQuery = useMemo(() => {
    return debounce((query: string) => {
      if (query.length === 0) {
        navigateFn({
          search: { ...omit(searchParameters, 'page', 'query') },
        });
        return;
      }
      navigateFn({
        search: { ...omit(searchParameters, 'page'), query },
      });
    }, 300);
  }, [navigateFn, searchParameters]);

  // Cancel on unmount
  useEffect(() => {
    return () => {
      updateQuery.cancel();
    };
  }, [updateQuery]);

  /**
   * Handle status toggle change.
   */
  const handleStatusChange = (newStatus: POPhase | 'all') => {
    const basePath = '/purchase-orders';
    if (newStatus === 'all') {
      navigateFn({
        to: basePath,
        search: {
          ...searchParameters,
          page: undefined,
          status: undefined,
          subStatuses: undefined,
        },
      });
    } else {
      navigateFn({
        to: basePath,
        search: {
          ...searchParameters,
          page: undefined,
          status: newStatus,
          subStatuses: undefined,
        },
      });
    }
  };

  const onPageChange = (pageNumber: number) => {
    navigateFn({
      search: { ...searchParameters, page: pageNumber },
    });
  };

  /**
   * Handle search input changes.
   */
  const handleChangeQuery: ChangeEventHandler<HTMLInputElement> = (event) => {
    updateQuery(event.target.value);
  };

  /**
   * Clear the search query.
   */
  const handleClearQuery = () => {
    if (searchContainer.current) {
      searchContainer.current.value = '';
    }
    updateQuery('');
  };

  /**
   * Handle modifier filter change.
   */
  const handleModifierChange = (subStatuses: POSubstatus[]) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        subStatuses: subStatuses.length > 0 ? subStatuses : undefined,
      },
    });
  };

  /**
   * Handle a change to the "from" location filter selection.
   */
  const handleChangeFromLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        fromLocations: locationIds.length > 0 ? locationIds : undefined,
        fromCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        fromLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  /**
   * Handle a change to the "to" location filter selection.
   */
  const handleChangeToLocations = (
    locationIds: string[],
    customerIds: string[],
    companyTypes: LocationCompanyType[]
  ) => {
    navigateFn({
      search: {
        ...searchParameters,
        page: undefined,
        toLocations: locationIds.length > 0 ? locationIds : undefined,
        toCustomerLocations: customerIds.length > 0 ? customerIds : undefined,
        toLocationCompanyTypes:
          companyTypes.length > 0 ? companyTypes : undefined,
      },
    });
  };

  const pageNumber = searchParameters.page ?? 1;
  const subStatuses = searchParameters.subStatuses ?? [];

  const fromLocationIds = searchParameters.fromLocations ?? [];
  const fromCustomerIds = searchParameters.fromCustomerLocations ?? [];
  const fromCompanyTypes = searchParameters.fromLocationCompanyTypes ?? [];
  const toLocationIds = searchParameters.toLocations ?? [];
  const toCustomerIds = searchParameters.toCustomerLocations ?? [];
  const toCompanyTypes = searchParameters.toLocationCompanyTypes ?? [];

  return (
    <Stack>
      <PurchaseOrderDialog
        isOpen={createDialog.isOpen}
        onClose={createDialog.close}
      />

      {/* Action bar with create button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterToggleGroup
            value={currentStatus}
            options={PO_STATUS_FILTER_OPTIONS}
            onChange={handleStatusChange}
            ariaLabel="purchase order status filter"
          />
          <IconButton
            ref={infoButtonRef}
            size="small"
            onClick={infoTooltip.open}
            aria-label="Learn about purchase orders"
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Stack>

        {/* Educational tooltip explaining purchase orders */}
        <EducationalTooltip
          title="Purchase orders"
          isOpen={infoTooltip.isOpen}
          onClose={infoTooltip.close}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2">
                <strong>Purchase orders</strong> track parts and products you
                purchase from suppliers. They represent your commitment to buy
                items and serve as the first step in the{' '}
                <strong>inventory workflow</strong> as items move toward
                becoming production assets.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Purchase order status
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Draft:</strong> Order is being prepared but not yet
                  sent to the supplier.
                </li>
                <li>
                  <strong>Awaiting 3PL:</strong> Order has been sent to the
                  third-party logistics provider and is awaiting fulfillment.
                </li>
                <li>
                  <strong>Open:</strong> Order has been confirmed and items are
                  awaiting shipment from the supplier.
                </li>
                <li>
                  <strong>Shipping:</strong> Supplier has shipped the items and
                  they are in transit.
                </li>
                <li>
                  <strong>Delivered:</strong> Items have arrived at the
                  destination and are awaiting receipt confirmation.
                </li>
                <li>
                  <strong>Received:</strong> All items in the order have been
                  received. Items are added to inventory through inbound
                  shipments.
                </li>
                <li>
                  <strong>Cancelled:</strong> The purchase order has been
                  cancelled and is no longer valid.
                </li>
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Fulfillment modifiers
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Each status includes a modifier indicating how the fulfilled
                quantity compares to the requested quantity:
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{ pl: 2, mb: 0, '& li': { mb: 0.5 } }}
              >
                <li>
                  <strong>Partial:</strong> Less than requested quantity has
                  been fulfilled
                </li>
                <li>
                  <strong>Full:</strong> Exactly the requested quantity has been
                  fulfilled
                </li>
                <li>
                  <strong>Overage:</strong> More than requested quantity has
                  been fulfilled
                </li>
              </Typography>
            </Box>
            <Typography variant="body2">
              <Link
                href="https://guide.hardfin.com/source-and-build/purchase-orders"
                target="_blank"
              >
                Read more about purchase orders
              </Link>
            </Typography>
          </Stack>
        </EducationalTooltip>

        <Button
          variant="contained"
          startIcon={<AddRounded fontSize="small" />}
          onClick={createDialog.open}
        >
          Add purchase order
        </Button>
      </Stack>

      {/* Filters and search */}
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}>
          <AssetLocationFilterMenu
            label="From location"
            locationIds={fromLocationIds}
            customerIds={fromCustomerIds}
            companyTypes={fromCompanyTypes}
            onChange={handleChangeFromLocations}
          />
          <AssetLocationFilterMenu
            label="To location"
            locationIds={toLocationIds}
            customerIds={toCustomerIds}
            companyTypes={toCompanyTypes}
            onChange={handleChangeToLocations}
          />
          {(currentStatus === 'all' ||
            PHASES_WITH_MODIFIERS.has(currentStatus)) && (
            <PurchaseOrderModifierFilter
              value={subStatuses}
              onChange={handleModifierChange}
            />
          )}
        </Stack>
        <TextField
          size="small"
          inputRef={searchContainer}
          placeholder="Search purchase orders"
          defaultValue={searchParameters.query}
          inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
          onChange={handleChangeQuery}
          InputProps={{
            endAdornment: searchParameters.query && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearQuery} edge="end" size="small">
                  <ClearRounded />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
        />
      </TableControlContainer>

      {/* Table */}
      <SupportErrorBoundary message="Failed to load purchase orders">
        <Suspense fallback={<PurchaseOrderTableSkeleton />}>
          <PurchaseOrderTable
            filter={{
              status: currentStatus === 'all' ? undefined : currentStatus,
              subStatuses: subStatuses.length > 0 ? subStatuses : undefined,
              searchTerm: searchParameters.query,
              page: pageNumber,
              pageSize: 20,
              fromLocationIds:
                fromLocationIds.length > 0 ? fromLocationIds : undefined,
              fromCustomerIds:
                fromCustomerIds.length > 0 ? fromCustomerIds : undefined,
              fromLocationCompanyTypes:
                fromCompanyTypes.length > 0 ? fromCompanyTypes : undefined,
              toLocationIds:
                toLocationIds.length > 0 ? toLocationIds : undefined,
              toCustomerIds:
                toCustomerIds.length > 0 ? toCustomerIds : undefined,
              toLocationCompanyTypes:
                toCompanyTypes.length > 0 ? toCompanyTypes : undefined,
              sort: sort,
            }}
            sort={sort}
            onSort={onSort}
            onPageChange={onPageChange}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
